/**
 * <announcement-bar> — accessible message slideshow.
 *
 * Shows one message at a time, centred, with previous/next chevron
 * controls at each edge (rendered only when there is more than one
 * message). Auto-advances on an interval, pausing on hover and keyboard
 * focus (WCAG 2.2.2) and never auto-advancing when the user prefers
 * reduced motion. Active state is driven by a `data-active` attribute so
 * all styling stays in CSS — no inline styles.
 */
class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this._items = [];
    this._index = 0;
    this._timer = null;
    this._interval = 5000;
    this._reduceMotion = null;
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._start = this._start.bind(this);
    this._stop = this._stop.bind(this);
  }

  connectedCallback() {
    this._items = Array.from(this.querySelectorAll('.announcement-bar__item'));
    if (!this._items.length) return;

    this._interval = parseInt(this.dataset.interval, 10) || 5000;
    this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._prevBtn = this.querySelector('[data-announcement-prev]');
    this._nextBtn = this.querySelector('[data-announcement-next]');
    if (this._prevBtn) this._prevBtn.addEventListener('click', this._onPrev);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._onNext);

    // Pause auto-rotation on hover / keyboard focus.
    this.addEventListener('mouseenter', this._stop);
    this.addEventListener('mouseleave', this._start);
    this.addEventListener('focusin', this._stop);
    this.addEventListener('focusout', this._start);

    // Take over slide state from the server-rendered data-active markup.
    this._show(0);
    this._start();
  }

  disconnectedCallback() {
    this._stop();
    if (this._prevBtn) this._prevBtn.removeEventListener('click', this._onPrev);
    if (this._nextBtn) this._nextBtn.removeEventListener('click', this._onNext);
    this.removeEventListener('mouseenter', this._stop);
    this.removeEventListener('mouseleave', this._start);
    this.removeEventListener('focusin', this._stop);
    this.removeEventListener('focusout', this._start);
  }

  _show(index) {
    const count = this._items.length;
    this._index = ((index % count) + count) % count;
    this._items.forEach((item, i) => {
      const active = i === this._index;
      item.setAttribute('data-active', active ? 'true' : 'false');
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  _onPrev() {
    this._show(this._index - 1);
    this._restart();
  }

  _onNext() {
    this._show(this._index + 1);
    this._restart();
  }

  _restart() {
    // A manual interaction resets the auto-advance timer.
    this._stop();
    this._start();
  }

  _start() {
    this._stop();
    if (this._items.length <= 1) return;
    if (this._reduceMotion && this._reduceMotion.matches) return;
    this._timer = window.setInterval(() => this._show(this._index + 1), this._interval);
  }

  _stop() {
    if (this._timer) {
      window.clearInterval(this._timer);
      this._timer = null;
    }
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
